/**
 * Patient Portal — /patient
 * Auth (sign-in / register / forgot), dashboard, book ride (with "for whom"
 * picker), ride detail w/ live driver map, cancel-with-reason, profile drawer
 * with family members CRUD. English + Español via patientI18n.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  User, LogOut, Plus, Calendar, MapPin, Phone, Car, ArrowLeft, Loader2,
  Edit3, ShieldCheck, XCircle, CheckCircle2, Clock, ChevronRight, DollarSign,
  Users, Trash2, Globe, Repeat, Share2, Copy, CalendarClock, Bell, BellOff,
  Navigation, CreditCard, Star, Bookmark, Wallet,
} from "lucide-react";
import { API } from "@/lib/api";
import { patientApi } from "@/lib/patientApi";
import { PatientAuthProvider, usePatientAuth } from "@/lib/patientAuth";
import { LangProvider, useLang } from "@/lib/patientI18n";
import { registerPatientServiceWorker, subscribePatient, pushSupported } from "@/lib/patientPush";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const money = (c) => (c ? `$${(c / 100).toFixed(2)}` : "—");
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const HOLD_AMOUNTS = {
  local_oneway: 4000, roundtrip_under10: 8000, dialysis_rt: 8000,
  weekly_dialysis_pkg: 25000, weekly_medical_pkg: 40000,
  discharge: 6500, other: 4000,
};
const TRIP_CLASSES = [
  { key: "local_oneway", range: "$40–$70" },
  { key: "roundtrip_under10", range: "$80–$150" },
  { key: "dialysis_rt", range: "$80–$120" },
  { key: "weekly_dialysis_pkg", range: "$250–$400" },
  { key: "weekly_medical_pkg", range: "$400–$500" },
  { key: "discharge", range: "$65–$120 · on-call" },
  { key: "other", range: "" },
];

/* ==================== Language switcher ==================== */

function LangSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  const toggle = () => setLang(lang === "en" ? "es" : "en");
  return (
    <button
      onClick={toggle}
      data-testid="lang-switch"
      className={`inline-flex items-center gap-1.5 rounded-full transition ${compact
        ? "bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5"
        : "bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-deep text-xs px-3 py-1.5 border border-brand-navy/15"}`}
      title="Switch language"
    >
      <Globe className="w-3.5 h-3.5" />
      <span className="font-medium">{lang === "en" ? "EN" : "ES"}</span>
      <span className="text-brand-muted">|</span>
      <span className="opacity-70">{lang === "en" ? "ES" : "EN"}</span>
    </button>
  );
}

/* ==================== Sign in / Register ==================== */

function AuthCard({ onSwitch, mode, initialEmail }) {
  const { login, register } = usePatientAuth();
  const { t } = useLang();
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await login({ email: email.trim().toLowerCase(), password });
        toast.success(t("auth_welcome_back"));
      } else if (mode === "register") {
        if (password.length < 8) throw new Error(t("auth_password_short"));
        await register({ email: email.trim().toLowerCase(), password, name, phone });
        toast.success(t("auth_account_created"));
      } else if (mode === "forgot") {
        await patientApi.forgot(forgotEmail.trim().toLowerCase());
        toast.success(t("auth_password_reset_sent"));
        onSwitch("signin");
      }
    } catch (err) {
      toast.error(err.message || t("auth_something_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-brand-navy flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{t("auth_portal")}</div>
              <div className="font-serif text-xl text-brand-deep leading-tight">{t("auth_org")}</div>
            </div>
          </div>
          <LangSwitch />
        </div>

        <form onSubmit={submit} className="bg-white border border-brand-navy/10 rounded-2xl p-7 shadow-sm space-y-4" data-testid={`patient-${mode}-form`}>
          <h1 className="font-serif text-2xl text-brand-deep">
            {mode === "signin" && t("auth_signin")}
            {mode === "register" && t("auth_register")}
            {mode === "forgot" && t("auth_forgot_title")}
          </h1>

          {mode === "register" && (
            <>
              <input placeholder={t("auth_full_name")} value={name} onChange={(e) => setName(e.target.value)} required data-testid="patient-name-input"
                className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
              <input placeholder={t("auth_phone")} value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="patient-phone-input"
                className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
            </>
          )}

          {mode !== "forgot" && (
            <input type="email" placeholder={t("auth_email")} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="patient-email-input"
              className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
          )}

          {mode === "forgot" && (
            <input type="email" placeholder={t("auth_email")} value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="patient-forgot-email"
              className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
          )}

          {mode !== "forgot" && (
            <input type="password" placeholder={mode === "register" ? t("auth_password_min") : t("auth_password")} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "register" ? 8 : undefined} data-testid="patient-password-input"
              className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
          )}

          <button type="submit" disabled={busy} data-testid={`patient-${mode}-submit`}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full transition disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === "signin" && t("auth_signin_submit")}
            {mode === "register" && t("auth_register_submit")}
            {mode === "forgot" && t("auth_forgot_submit")}
          </button>

          <div className="text-xs text-center text-brand-muted pt-2">
            {mode === "signin" && (
              <>
                <button type="button" onClick={() => onSwitch("forgot")} className="text-brand-green hover:underline" data-testid="switch-to-forgot">{t("auth_forgot_link")}</button>
                {" · "}
                {t("auth_new_here")} <button type="button" onClick={() => onSwitch("register")} className="text-brand-green hover:underline" data-testid="switch-to-register">{t("auth_create_account")}</button>
              </>
            )}
            {mode === "register" && (<>{t("auth_already")} <button type="button" onClick={() => onSwitch("signin")} className="text-brand-green hover:underline" data-testid="switch-to-signin">{t("auth_signin")}</button></>)}
            {mode === "forgot" && (<button type="button" onClick={() => onSwitch("signin")} className="text-brand-green hover:underline">{t("auth_back_signin")}</button>)}
          </div>
        </form>

        <p className="text-center text-xs text-brand-muted mt-6 px-4 leading-relaxed">{t("auth_hipaa")}</p>
      </div>
    </div>
  );
}

/* ==================== Address autocomplete (Photon) ==================== */

function AddressAutocomplete({ value, onChange, placeholder, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const abortRef = useRef(null);

  const label = (f) => {
    const p = f.properties || {};
    return [p.name, p.housenumber ? `${p.housenumber} ${p.street || ""}`.trim() : (p.street || ""),
      p.city || p.town || p.village, p.state, p.postcode, p.country]
      .filter(Boolean).join(", ");
  };

  const handleChange = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (abortRef.current) abortRef.current.abort();
    if (v.trim().length < 3) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(v)}&limit=6&lang=en`, { signal: ctrl.signal });
        const data = await r.json();
        setSuggestions(data.features || []);
        setOpen(true);
      } catch (e) { /* silent */ }
    }, 300);
  };

  return (
    <div className="relative">
      <input value={value} onChange={(e) => handleChange(e.target.value)}
        onFocus={() => value.length >= 3 && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder} data-testid={testid}
        className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 bg-white border border-brand-navy/15 rounded-b-lg max-h-64 overflow-auto shadow-lg">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => {
              const coords = (s.geometry && s.geometry.coordinates) || null;
              onChange(label(s), coords ? { lng: coords[0], lat: coords[1] } : undefined);
              setOpen(false); setSuggestions([]);
            }}
              className="w-full text-left px-4 py-2.5 text-sm text-brand-deep hover:bg-brand-surfaceAlt border-b border-brand-navy/5 last:border-b-0">
              {label(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== Book ride form ==================== */

function BookRide({ user, onBack, onBooked, prefill }) {
  const { t } = useLang();
  const [forMemberId, setForMemberId] = useState("self");
  const [payerType, setPayerType] = useState(user.medicaid_id ? "medicaid" : "private");
  const [payOnArrival, setPayOnArrival] = useState(false);
  const [preferredMethod, setPreferredMethod] = useState("card_on_file");
  const [tripClass, setTripClass] = useState(prefill?.trip_class || "local_oneway");
  const [pickup, setPickup] = useState(prefill?.pickup || user.home_address || "");
  const [dropoff, setDropoff] = useState(prefill?.dropoff || "");
  // Stops + return-different-address (added Feb 2026 for multi-stop trips
  // like pharmacy pickup on the way to a doctor visit).
  const [stops, setStops] = useState([]);
  const [returnDiff, setReturnDiff] = useState(false);
  const [returnAddress, setReturnAddress] = useState("");
  const [tripDate, setTripDate] = useState("");
  const [tripTime, setTripTime] = useState("");
  const [mobility, setMobility] = useState(prefill?.mobility || "ambulatory");
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [waitMinutes, setWaitMinutes] = useState(0);
  const [insuranceMemberId, setInsuranceMemberId] = useState(user.medicaid_id || "");
  const [insurancePlan, setInsurancePlan] = useState(user.insurance_plan || "");
  const [broker, setBroker] = useState("");
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const [repeatDays, setRepeatDays] = useState([]);
  const [busy, setBusy] = useState(false);

  const family = user.family_members || [];

  // When a family member is picked, prefill their data for the ride
  const applyForMember = (mid) => {
    setForMemberId(mid);
    if (mid === "self") {
      setPickup(user.home_address || "");
      setInsuranceMemberId(user.medicaid_id || "");
      setInsurancePlan(user.insurance_plan || "");
      setPayerType(user.medicaid_id ? "medicaid" : "private");
      return;
    }
    const f = family.find((x) => x.id === mid);
    if (!f) return;
    setPickup(f.home_address || user.home_address || "");
    setInsuranceMemberId(f.medicaid_id || "");
    setInsurancePlan(f.insurance_plan || "");
    setPayerType(f.medicaid_id ? "medicaid" : "private");
  };

  const minDate = useMemo(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().split("T")[0];
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pickup || !dropoff || !tripDate || !tripTime) {
      toast.error(t("book_fill_required"));
      return;
    }
    setBusy(true);
    try {
      const result = await patientApi.book({
        origin_url: window.location.origin,
        payer_type: payerType,
        payment_mode: (payerType === "private" && preferredMethod !== "card_on_file") ? "on_arrival" : "prepaid",
        preferred_payment_method: payerType === "private" ? preferredMethod : "card_on_file",
        // 1-tap booking: if patient has a saved card AND they picked "card on file",
        // skip Stripe Checkout — hold is placed off-session, ride is `held` immediately.
        use_saved_card: !!(payerType === "private" && preferredMethod === "card_on_file" && user?.default_card_last4),
        wait_minutes: Number(waitMinutes) || 0,
        pickup, dropoff,
        // Multi-stop + separate-return support. The backend geocodes any
        // stops/return address that were entered as plain text so the fare
        // reflects real leg-by-leg mileage.
        stops: (stops || []).filter((s) => s && s.address).map((s) => ({
          address: s.address,
          lat: s.lat || null,
          lng: s.lng || null,
        })),
        return_address: returnDiff && returnAddress ? returnAddress : null,
        trip_date: tripDate, trip_time: tripTime,
        trip_class: tripClass, mobility,
        on_call: tripClass === "discharge",
        priority: tripClass === "discharge" ? "high" : "normal",
        purpose: purpose || null, notes: notes || null,
        insurance_member_id: insuranceMemberId || null,
        insurance_plan: insurancePlan || null,
        broker: broker || null,
        for_member_id: forMemberId,
        repeat_weekly: repeatWeekly,
        repeat_weeks: repeatWeeks,
        // Ensure the base date's weekday is included so backend never 400s
        repeat_days: repeatWeekly
          ? (() => {
              const baseWd = (new Date(tripDate + "T00:00:00").getDay() + 6) % 7; // Mon=0
              const set = new Set(repeatDays);
              set.add(baseWd);
              return Array.from(set).sort();
            })()
          : [],
      });
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      if (result.status === "held" && result.card_last4) {
        toast.success(`Ride booked · ${(result.card_brand || "card").toUpperCase()} ····${result.card_last4} · $${(result.hold_amount_cents/100).toFixed(2)} held`);
      } else if (result.series_id) {
        toast.success(t("recur_success", result.series_count));
      } else {
        toast.success(t("book_success"));
      }
      onBooked();
    } catch (err) {
      toast.error(err.message || t("book_failed"));
    } finally {
      setBusy(false);
    }
  };

  const holdAmt = HOLD_AMOUNTS[tripClass] || 0;

  return (
    <div className="min-h-screen bg-brand-bg pb-24">
      <div className="bg-brand-deep text-white px-5 py-5 flex items-center gap-3">
        <button onClick={onBack} data-testid="book-back" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">{t("book_new")}</div>
          <div className="font-serif text-xl">{t("book_schedule")}</div>
        </div>
        <LangSwitch compact />
      </div>

      <form onSubmit={submit} className="max-w-2xl mx-auto p-5 space-y-5" data-testid="patient-book-form">
        {/* For whom */}
        {family.length > 0 && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">{t("book_for")}</div>
            <div className="flex flex-wrap gap-2">
              <label className={`px-4 py-2.5 rounded-full border text-sm font-medium cursor-pointer transition ${forMemberId === "self" ? "border-brand-green bg-emerald-50 text-brand-green" : "border-brand-navy/15 text-brand-deep"}`}>
                <input type="radio" name="for_member" value="self" checked={forMemberId === "self"} onChange={() => applyForMember("self")} className="hidden" data-testid="for-self" />
                {t("book_for_me")} · {(user.name || "").split(" ")[0]}
              </label>
              {family.map((f) => (
                <label key={f.id} className={`px-4 py-2.5 rounded-full border text-sm font-medium cursor-pointer transition ${forMemberId === f.id ? "border-brand-green bg-emerald-50 text-brand-green" : "border-brand-navy/15 text-brand-deep"}`}>
                  <input type="radio" name="for_member" value={f.id} checked={forMemberId === f.id} onChange={() => applyForMember(f.id)} className="hidden" data-testid={`for-${f.id}`} />
                  {f.name}{f.relationship ? ` · ${f.relationship}` : ""}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Payer type */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">{t("book_who_paying")}</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: "private", l: t("book_payer_private") },
              { k: "medicaid", l: t("book_payer_medicaid") },
              { k: "medicare", l: t("book_payer_medicare") },
            ].map(({ k, l }) => (
              <label key={k} className={`px-3 py-2.5 rounded-lg border text-center text-sm font-medium cursor-pointer transition ${payerType === k ? "border-brand-green bg-emerald-50 text-brand-green" : "border-brand-navy/15 text-brand-deep"}`}>
                <input type="radio" name="payer" value={k} checked={payerType === k} onChange={() => setPayerType(k)} className="hidden" data-testid={`payer-${k}`} />
                {l}
              </label>
            ))}
          </div>
          {payerType !== "private" && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={insuranceMemberId} onChange={(e) => setInsuranceMemberId(e.target.value)} placeholder={t("book_member_id")} data-testid="insurance-id" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
              <input value={insurancePlan} onChange={(e) => setInsurancePlan(e.target.value)} placeholder={t("book_plan")} data-testid="insurance-plan" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
              <input value={broker} onChange={(e) => setBroker(e.target.value)} placeholder={t("book_broker")} data-testid="broker" className="sm:col-span-2 px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            </div>
          )}
        </div>

        {/* Trip class */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">{t("book_trip_type")}</div>
          <div className="space-y-2">
            {TRIP_CLASSES.map((tc) => (
              <label key={tc.key} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${tripClass === tc.key ? "border-brand-green bg-emerald-50" : "border-brand-navy/15"}`}>
                <div>
                  <input type="radio" name="trip_class" value={tc.key} checked={tripClass === tc.key} onChange={() => setTripClass(tc.key)} className="hidden" data-testid={`class-${tc.key}`} />
                  <div className="text-sm font-medium text-brand-deep">{t(`tc_${tc.key}`)}</div>
                  {tc.range && <div className="text-xs text-brand-muted mt-0.5">{tc.range}</div>}
                </div>
                {payerType === "private" && tripClass === tc.key && holdAmt > 0 && (
                  <div className="text-xs font-mono font-semibold text-brand-green">{t("ride_hold_lbl")} {money(holdAmt)}</div>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Addresses */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold mb-2">{t("book_pickup")}</div>
            <AddressAutocomplete value={pickup} onChange={setPickup} placeholder={t("book_pickup_ph")} testid="pickup-input" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-navy font-semibold mb-2">{t("book_dropoff")}</div>
            <AddressAutocomplete value={dropoff} onChange={setDropoff} placeholder={t("book_dropoff_ph")} testid="dropoff-input" />
          </div>
        </div>

        {/* Stops + separate return address */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-navy font-semibold">Extra stops & return</div>
          </div>
          {stops.map((s, i) => (
            <div key={i} className="relative" data-testid={`stop-row-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted">Stop {i + 1}</span>
                <button type="button" onClick={() => setStops(stops.filter((_, j) => j !== i))}
                  data-testid={`remove-stop-${i}`} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
              <AddressAutocomplete
                value={s.address || ""}
                onChange={(v, extras) => {
                  const next = [...stops];
                  next[i] = { ...next[i], address: v, lat: extras?.lat || null, lng: extras?.lng || null };
                  setStops(next);
                }}
                placeholder="Stop address (e.g. CVS Pharmacy)"
                testid={`stop-input-${i}`}
              />
            </div>
          ))}
          <button type="button" onClick={() => setStops([...stops, { address: "", lat: null, lng: null }])}
            data-testid="add-stop-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:underline">
            <Plus className="w-4 h-4" /> Add a stop (pharmacy, side visit, etc.)
          </button>
          <div className="border-t border-brand-navy/10 pt-3">
            <label className="inline-flex items-center gap-2 text-sm text-brand-deep cursor-pointer">
              <input type="checkbox" checked={returnDiff} onChange={(e) => setReturnDiff(e.target.checked)} data-testid="return-diff-toggle" />
              Return trip goes to a different address
            </label>
            {returnDiff && (
              <div className="mt-2" data-testid="return-address-wrap">
                <AddressAutocomplete value={returnAddress} onChange={setReturnAddress} placeholder="Return address" testid="return-input" />
              </div>
            )}
          </div>
          <p className="text-xs text-brand-muted">Each stop is added to the total mileage — charged at the same per-mile rate.</p>
        </div>

        {/* Date + Time */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{t("book_when")}</div>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={tripDate} min={minDate} onChange={(e) => setTripDate(e.target.value)} required data-testid="trip-date" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input type="time" value={tripTime} onChange={(e) => setTripTime(e.target.value)} required data-testid="trip-time" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          </div>
          <div className="text-xs text-brand-muted">{t("book_24h")}</div>
        </div>

        {/* Mobility + notes */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{t("book_mobility")}</div>
          <div className="flex flex-wrap gap-2">
            {["ambulatory", "wheelchair", "stretcher", "assist_only"].map((m) => (
              <label key={m} className={`px-3 py-2 rounded-full border text-xs font-medium cursor-pointer transition ${mobility === m ? "border-brand-green bg-emerald-50 text-brand-green" : "border-brand-navy/15 text-brand-deep"}`}>
                <input type="radio" name="mobility" value={m} checked={mobility === m} onChange={() => setMobility(m)} className="hidden" data-testid={`mobility-${m}`} />
                {t(`mob_${m}`)}
              </label>
            ))}
          </div>
          <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t("book_purpose_ph")} data-testid="purpose-input" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <div>
            <label className="block text-xs text-brand-muted mb-1">Expected wait at pickup (minutes)</label>
            <input
              type="number" min={0} max={240} step={5}
              value={waitMinutes}
              onChange={(e) => setWaitMinutes(e.target.value)}
              data-testid="wait-minutes-input"
              placeholder="0"
              className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15"
            />
            <p className="text-[11px] text-brand-muted mt-1 leading-snug">
              If your driver needs to wait while you finish an appointment (dialysis, doctor visit, etc.), enter the expected wait time. Prevents your driver from being auto-flagged for a no-show after {20} minutes.
            </p>
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={t("book_notes_ph")} data-testid="notes-input" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15 resize-none" />
        </div>

        {/* Recurring */}
        <div className={`rounded-2xl border p-5 space-y-3 transition ${repeatWeekly ? "border-brand-green bg-emerald-50/40" : "border-brand-navy/10 bg-white"}`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={repeatWeekly}
              onChange={(e) => setRepeatWeekly(e.target.checked)}
              data-testid="recur-toggle"
              className="w-4 h-4 accent-emerald-600"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-brand-deep flex items-center gap-2">
                <Repeat className="w-4 h-4" /> {t("recur_toggle")}
              </div>
              {repeatWeekly && payerType === "private" && (
                <div className="text-xs text-amber-700 mt-1">
                  Card holds expire after 7 days. We'll email you to authorize each week's hold 3 days before the trip.
                </div>
              )}
            </div>
          </label>

          {repeatWeekly && (
            <div className="pt-2 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted font-semibold mb-1">{t("recur_days")}</div>
                <div className="text-xs text-brand-muted mb-2">{t("recur_days_hint")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { i: 0, k: "day_mon" }, { i: 1, k: "day_tue" }, { i: 2, k: "day_wed" },
                    { i: 3, k: "day_thu" }, { i: 4, k: "day_fri" }, { i: 5, k: "day_sat" }, { i: 6, k: "day_sun" },
                  ].map(({ i, k }) => {
                    const active = repeatDays.includes(i);
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => setRepeatDays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i])}
                        data-testid={`recur-day-${i}`}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${active ? "border-brand-green bg-brand-green text-white" : "border-brand-navy/15 text-brand-deep hover:border-brand-green"}`}
                      >
                        {t(k)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted font-semibold mb-1">{t("recur_weeks")}</div>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={repeatWeeks}
                  onChange={(e) => setRepeatWeeks(Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1)))}
                  data-testid="recur-weeks"
                  className="w-24 px-3 py-2 rounded-lg border border-brand-navy/15"
                />
              </div>
            </div>
          )}
        </div>

        {payerType === "private" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-900">
            {preferredMethod === "card_on_file"
              ? <>A 50% pre-authorization hold (~<strong>{money(Math.round(holdAmt * 0.5))}</strong>) will be placed on your card. Only the actual trip total is captured after completion.</>
              : <>A 70% pre-authorization hold (~<strong>{money(Math.round(holdAmt * 0.7))}</strong>) will be placed on your card as a guarantee. It&rsquo;s released after you pay the driver by {preferredMethod}.</>}
          </div>
        )}

        {payerType === "private" && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="payment-method-picker">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Preferred payment method</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: "card_on_file", label: user?.default_card_last4 ? `Card ····${user.default_card_last4}` : "Card on file", sub: user?.default_card_last4 ? "1-tap · 50% hold" : "50% hold · card is charged", emoji: "💳" },
                { id: "zelle", label: "Zelle / Check", sub: "70% hold · 48h notice", emoji: "📱" },
              ].map((opt) => (
                <label
                  key={opt.id}
                  className={`cursor-pointer rounded-xl border-2 p-3 transition ${
                    preferredMethod === opt.id ||
                    (opt.id === "zelle" && (preferredMethod === "check" || preferredMethod === "zelle"))
                      ? "border-brand-green bg-emerald-50"
                      : "border-brand-navy/10 hover:border-brand-navy/30"
                  }`}
                  data-testid={`method-${opt.id}`}
                >
                  <input
                    type="radio"
                    name="preferred_method"
                    value={opt.id}
                    checked={preferredMethod === opt.id}
                    onChange={() => { setPreferredMethod(opt.id); setPayOnArrival(opt.id !== "card_on_file"); }}
                    className="sr-only"
                  />
                  <div className="text-2xl">{opt.emoji}</div>
                  <div className="font-semibold text-sm text-brand-deep mt-1">{opt.label}</div>
                  <div className="text-[11px] text-brand-muted mt-0.5 leading-tight">{opt.sub}</div>
                </label>
              ))}
            </div>
            {preferredMethod !== "card_on_file" && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid="offline-method-notice">
                <strong>Heads up:</strong> {preferredMethod === "check" ? "check" : "Zelle"} bookings must be scheduled <strong>at least 48 hours in advance</strong>. Your card is still required on file — 70% is held as a guarantee and released once the driver confirms payment.
              </div>
            )}
            <div className="mt-2 text-[11px] text-brand-muted italic">
              Need to pay cash? Call dispatch at (404) 590-4621 to arrange it — cash rides require pre-approval and 48h notice.
            </div>
          </div>
        )}

        {/* Live estimate for private-pay rides */}
        {payerType === "private" && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="book-estimate-panel">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Estimated cost</div>
              <a href="/estimate" target="_blank" rel="noreferrer" className="text-xs text-brand-navy hover:text-brand-green underline">Open full calculator →</a>
            </div>
            <BookEstimate
              tripClass={tripClass}
              mobility={mobility}
              pickup={pickup}
              dropoff={dropoff}
            />
          </div>
        )}

        <button type="submit" disabled={busy} data-testid="book-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-semibold py-4 rounded-full transition disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
          {payerType === "private"
            ? (preferredMethod === "card_on_file"
                ? t("book_submit_private", money(Math.round(holdAmt * 0.5)))
                : t("book_submit_private", money(Math.round(holdAmt * 0.7))) + " (guarantee)")
            : t("book_submit_ins")}
        </button>
      </form>
    </div>
  );
}

/* ==================== Ride detail ==================== */

function RideDetail({ rideId, onBack, onRefresh }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const load = async () => {
    try {
      const d = await patientApi.getRide(rideId);
      setData(d);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rideId]);
  useEffect(() => {
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
    /* eslint-disable-next-line */
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  if (!data) return null;
  const { ride, driver, eta } = data;

  const showLiveMap = driver && driver.last_location &&
    ["accepted", "en_route", "arrived", "in_progress"].includes(ride.driver_ride_status);
  const isActive = ["held", "insurance_pending", "pending_payment", "series_pending_payment"].includes(ride.status);
  const isDriverActive = ["accepted", "en_route", "arrived", "in_progress"].includes(ride.driver_ride_status);
  const canReschedule = isActive && !isDriverActive;
  const canShare = isActive || isDriverActive;
  const needsPay = ride.status === "series_pending_payment";

  const cancelSeries = async () => {
    if (!window.confirm(t("recur_cancel_confirm"))) return;
    try {
      const res = await patientApi.cancelSeries(ride.series_id);
      toast.success(t("recur_cancel_success", res.cancelled));
      onRefresh();
      onBack();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg pb-16">
      <div className="bg-brand-deep text-white px-5 py-5 flex items-center gap-3">
        <button onClick={onBack} data-testid="ride-back" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">{t("ride_details")}</div>
          <div className="font-serif text-xl">{ride.trip_date} · {ride.trip_time}</div>
        </div>
        <LangSwitch compact />
      </div>

      <div className="max-w-2xl mx-auto p-5 space-y-4">
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted font-semibold">{t("ride_status")}</div>
            <div className="font-serif text-xl text-brand-deep mt-1">{titleCase(ride.status)}</div>
            {ride.driver_ride_status && (
              <div className="text-xs text-brand-muted mt-1">{t("ride_driver_status")}: <strong>{ride.driver_ride_status === "en_route" ? "On the way" : titleCase(ride.driver_ride_status)}</strong></div>
            )}
            {ride.for_kind === "family" && ride.for_name && (
              <div className="text-xs text-brand-green mt-1"><Users className="w-3 h-3 inline mr-1" /> {t("book_for")}: <strong>{ride.for_name}</strong></div>
            )}
            {ride.series_id && (
              <div className="text-xs text-brand-navy mt-1"><Repeat className="w-3 h-3 inline mr-1" /> {t("recur_series_badge", ride.series_index, ride.series_total)}</div>
            )}
          </div>
          {ride.status === "held" && <ShieldCheck className="w-8 h-8 text-emerald-500" />}
          {ride.status === "completed" && <CheckCircle2 className="w-8 h-8 text-emerald-500" />}
          {ride.status === "cancelled" && <XCircle className="w-8 h-8 text-slate-400" />}
        </div>

        {ride.status === "completed" && (
          <RatingCard ride={ride} onSaved={() => { load(); onRefresh(); }} />
        )}

        {needsPay && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="pay-hold-card">
            <div className="flex items-start gap-3 mb-3">
              <CreditCard className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-serif text-lg text-amber-900">{t("pay_hold_title")}</div>
                <div className="text-xs text-brand-muted mt-1">
                  {t("pay_hold_body", `$${((ride.hold_amount_cents || 4000) / 100).toFixed(2)}`)}
                </div>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const { checkout_url } = await patientApi.payHold(ride.id);
                  if (checkout_url) window.location.href = checkout_url;
                } catch (err) {
                  toast.error(err.message);
                }
              }}
              data-testid="pay-hold-cta"
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-full transition"
            >
              <CreditCard className="w-4 h-4" /> {t("pay_hold_cta", `$${((ride.hold_amount_cents || 4000) / 100).toFixed(2)}`)}
            </button>
          </div>
        )}

        {driver && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="ride-driver-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{t("ride_your_driver")}</div>
              {eta && ["accepted", "en_route", "arrived"].includes(ride.driver_ride_status) && (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="eta-badge">
                  <Navigation className="w-3.5 h-3.5" />
                  {ride.driver_ride_status === "arrived" ? t("eta_at_pickup") : t("eta_mins_away", eta.minutes)}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              {driver.has_photo ? (
                <img src={`${API}/nemt/driver/photo/${driver.id}`} alt={driver.name} className="w-16 h-16 rounded-full object-cover border-2 border-brand-navy/10" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-brand-surfaceAlt flex items-center justify-center"><User className="w-6 h-6 text-brand-muted" /></div>
              )}
              <div className="flex-1">
                <div className="font-serif text-lg text-brand-deep">{driver.name}</div>
                <div className="text-sm text-brand-muted mt-1 flex items-center gap-2 flex-wrap">
                  <Car className="w-3.5 h-3.5" />
                  {[driver.car_year, driver.car_color, driver.car_make, driver.car_model].filter(Boolean).join(" ") || t("ride_vehicle_fallback")}
                </div>
                {driver.car_tag && (<div className="text-xs font-mono text-brand-deep mt-1">{t("ride_tag")}: <strong>{driver.car_tag}</strong></div>)}
              </div>
              <a href={`tel:${(driver.phone || "").replace(/[^0-9+]/g, "")}`} className="w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition">
                <Phone className="w-5 h-5" />
              </a>
            </div>

            {showLiveMap && (
              <div className="mt-4 rounded-xl overflow-hidden border border-brand-navy/10" style={{ height: 240 }} data-testid="ride-live-map">
                <MapContainer center={[driver.last_location.lat, driver.last_location.lng]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[driver.last_location.lat, driver.last_location.lng]}>
                    <Popup><div className="text-sm"><div className="font-semibold">{driver.name}</div></div></Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{t("ride_trip")}</div>
          <div className="text-sm text-brand-deep space-y-2">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><strong>{t("ride_pickup_lbl")}</strong> {ride.pickup}</span></div>
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-brand-navy mt-0.5 shrink-0" /><span><strong>{t("ride_dropoff_lbl")}</strong> {ride.dropoff}</span></div>
            <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span>{ride.trip_date} · {ride.trip_time}</span></div>
            {ride.hold_amount_cents ? (<div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span>{t("ride_hold_lbl")} <strong>{money(ride.hold_amount_cents)}</strong></span></div>) : null}
            {ride.captured_amount_cents ? (<div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>{t("ride_captured_lbl")} <strong>{money(ride.captured_amount_cents)}</strong></span></div>) : null}
            {ride.cancellation_fee_cents ? (<div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>{t("ride_cancel_fee_lbl")} <strong>{money(ride.cancellation_fee_cents)}</strong></span></div>) : null}
            {ride.status === "cancelled" && !ride.cancellation_fee_cents && (<div className="text-emerald-700 font-medium">{t("ride_fee_waived")}</div>)}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {canShare && (
            <button onClick={() => setShowShare(true)} data-testid="ride-share-open"
              className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full transition">
              <Share2 className="w-4 h-4" /> {t("share_button")}
            </button>
          )}
          {canReschedule && (
            <button onClick={() => setShowReschedule(true)} data-testid="ride-reschedule-open"
              className="inline-flex items-center justify-center gap-2 border border-brand-navy/20 text-brand-deep hover:bg-brand-surfaceAlt font-medium py-3 rounded-full transition">
              <CalendarClock className="w-4 h-4" /> {t("resched_button")}
            </button>
          )}
        </div>

        {isActive && (
          <button onClick={() => setShowCancel(true)} data-testid="ride-cancel-open"
            className="w-full inline-flex items-center justify-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 font-medium py-3 rounded-full transition">
            <XCircle className="w-4 h-4" /> {t("ride_cancel_button")}
          </button>
        )}

        {ride.series_id && isActive && (
          <button onClick={cancelSeries} data-testid="series-cancel-remaining"
            className="w-full text-xs text-red-600 hover:text-red-800 hover:underline text-center pt-1">
            {t("recur_cancel_remaining")}
          </button>
        )}
      </div>

      {showCancel && (
        <CancelDialog ride={ride} onClose={() => setShowCancel(false)} onConfirmed={() => { setShowCancel(false); load(); onRefresh(); }} busy={cancelling} setBusy={setCancelling} />
      )}
      {showReschedule && (
        <RescheduleDialog ride={ride} onClose={() => setShowReschedule(false)} onSaved={() => { setShowReschedule(false); load(); onRefresh(); }} />
      )}
      {showShare && (
        <ShareDialog ride={ride} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

/* ==================== Compact booking-form estimate ==================== */

function BookEstimate({ tripClass, mobility, pickup, dropoff }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      // Only fetch when there's enough context (either fixed class or address text)
      if (!tripClass) return;
      setLoading(true);
      try {
        const body = {
          trip_class: tripClass,
          mobility,
        };
        // If addresses have length, try to geocode via Photon quickly to get miles
        if (pickup && dropoff && tripClass === "local_oneway") {
          try {
            const [p, d] = await Promise.all([
              fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(pickup)}&limit=1`).then((r) => r.json()),
              fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(dropoff)}&limit=1`).then((r) => r.json()),
            ]);
            if (p.features?.[0] && d.features?.[0]) {
              body.pickup_lng = p.features[0].geometry.coordinates[0];
              body.pickup_lat = p.features[0].geometry.coordinates[1];
              body.dropoff_lng = d.features[0].geometry.coordinates[0];
              body.dropoff_lat = d.features[0].geometry.coordinates[1];
            }
          } catch (e) { /* silent */ }
        }
        const res = await fetch(`${API}/nemt/estimate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (res.ok) setResult(data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    }, 700);
    return () => clearTimeout(timerRef.current);
  }, [tripClass, mobility, pickup, dropoff]);

  if (loading && !result) return <div className="text-xs text-brand-muted"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Calculating…</div>;
  if (!result) return <div className="text-xs text-brand-muted">Pick a trip type to see an estimate.</div>;

  return (
    <div>
      <div className="font-serif text-2xl text-brand-deep" data-testid="book-estimate-range">
        {money(result.low_cents)} – {money(result.high_cents)}
      </div>
      {result.miles && <div className="text-xs text-brand-muted mt-1">{result.miles} miles</div>}
      <div className="mt-3 text-xs text-brand-muted italic">{result.note}</div>
    </div>
  );
}

/* ==================== Rating card ==================== */

function RatingCard({ ride, onSaved }) {
  const { t } = useLang();
  const [stars, setStars] = useState(ride.rating_stars || 0);
  const [comment, setComment] = useState(ride.rating_comment || "");
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(Boolean(ride.rating_stars));

  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      await patientApi.rate(ride.id, { stars, comment: comment.trim() || null });
      setSaved(true);
      toast.success(t("rate_thanks"));
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const shown = hover || stars;
  const label = shown ? t(`rate_stars_${shown}`) : "";

  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="rating-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-xl text-brand-deep">
          {saved ? t("rate_your_rating") : t("rate_title")}
        </h3>
        {saved && (
          <button onClick={() => setSaved(false)} data-testid="rating-edit"
            className="text-xs text-brand-navy hover:text-brand-green">{t("rate_edit")}</button>
        )}
      </div>
      {!saved && <p className="text-xs text-brand-muted mb-4">{t("rate_subtitle")}</p>}

      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => !saved && setHover(n)}
            onMouseLeave={() => !saved && setHover(0)}
            onClick={() => !saved && setStars(n)}
            disabled={saved}
            data-testid={`rating-star-${n}`}
            aria-label={`Rate ${n} out of 5`}
            className="p-1 disabled:cursor-default transition"
          >
            <Star
              className={`w-8 h-8 transition ${
                n <= shown ? "fill-amber-400 text-amber-500" : "text-slate-300"
              }`}
            />
          </button>
        ))}
        <span className="ml-3 text-sm font-medium text-brand-deep">{label}</span>
      </div>

      {!saved && (
        <>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder={t("rate_comment_ph")}
            data-testid="rating-comment"
            maxLength={1000}
            className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15 text-sm resize-none mb-3"
          />
          <button
            onClick={submit}
            disabled={!stars || busy}
            data-testid="rating-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("rate_submit")}
          </button>
        </>
      )}
      {saved && ride.rating_comment && (
        <div className="text-sm text-brand-muted italic mt-2">"{ride.rating_comment}"</div>
      )}
    </div>
  );
}

/* ==================== Reschedule dialog ==================== */

function RescheduleDialog({ ride, onClose, onSaved }) {
  const { t } = useLang();
  const [tripDate, setTripDate] = useState(ride.trip_date || "");
  const [tripTime, setTripTime] = useState(ride.trip_time || "");
  const [busy, setBusy] = useState(false);

  const minDate = useMemo(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().split("T")[0];
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await patientApi.reschedule(ride.id, { trip_date: tripDate, trip_time: tripTime });
      toast.success(t("resched_success"));
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="reschedule-dialog">
        <h2 className="font-serif text-2xl text-brand-deep mb-1">{t("resched_title")}</h2>
        <p className="text-sm text-brand-muted mb-4">{t("resched_intro")}</p>

        <label className="text-xs font-medium text-brand-deep">{t("resched_new_date")}</label>
        <input type="date" value={tripDate} min={minDate} onChange={(e) => setTripDate(e.target.value)} required data-testid="resched-date"
          className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15 mb-3" />

        <label className="text-xs font-medium text-brand-deep">{t("resched_new_time")}</label>
        <input type="time" value={tripTime} onChange={(e) => setTripTime(e.target.value)} required data-testid="resched-time"
          className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15 mb-4" />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-deep text-sm font-medium">{t("cancel_keep").split(" ")[0]}</button>
          <button type="submit" disabled={busy} data-testid="resched-confirm" className="flex-1 px-4 py-2.5 rounded-full bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : t("resched_confirm")}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ==================== Share dialog ==================== */

function ShareDialog({ ride, onClose }) {
  const { t } = useLang();
  const [state, setState] = useState({ loading: true, url: "", expires: "" });

  useEffect(() => {
    (async () => {
      try {
        const res = await patientApi.share(ride.id);
        const url = `${window.location.origin}${res.share_path}`;
        setState({ loading: false, url, expires: res.expires_at });
      } catch (err) {
        toast.error(err.message);
        onClose();
      }
    })();
    /* eslint-disable-next-line */
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.url);
      toast.success(t("share_copied"));
    } catch (e) {
      // fallback: prompt
      window.prompt(t("share_copy"), state.url);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="share-dialog">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Share2 className="w-5 h-5" />
          </div>
          <h2 className="font-serif text-xl text-brand-deep">{t("share_title")}</h2>
        </div>
        <p className="text-sm text-brand-muted mb-4">{t("share_intro")}</p>

        {state.loading ? (
          <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-brand-green" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <input value={state.url} readOnly data-testid="share-url" className="flex-1 px-3 py-2.5 rounded-lg border border-brand-navy/15 bg-brand-surfaceAlt text-xs font-mono" />
              <button onClick={copy} data-testid="share-copy" className="p-2.5 rounded-lg bg-brand-navy hover:bg-brand-navyDark text-white transition">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-deep text-sm font-medium">{t("cancel_keep").split(" ")[0]}</button>
          </>
        )}
      </div>
    </div>
  );
}

function CancelDialog({ ride, onClose, onConfirmed, busy, setBusy }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");

  const confirm = async () => {
    if (!reason) { toast.error(t("cancel_pick_reason")); return; }
    setBusy(true);
    try {
      const res = await patientApi.cancelRide(ride.id, reason, detail || null);
      toast.success(res.fee_waived ? t("cancel_success_waived") : t("cancel_success_fee", money(res.cancellation_fee_cents)));
      setTimeout(() => onConfirmed(), 900);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="cancel-dialog">
        <h2 className="font-serif text-2xl text-brand-deep mb-1">{t("cancel_title")}</h2>
        <p className="text-sm text-brand-muted mb-4">{t("cancel_intro")}</p>

        <div className="space-y-2 mb-4">
          {[
            { k: "doctor_cancelled", label: t("cancel_reason_doctor") },
            { k: "feeling_unwell", label: t("cancel_reason_unwell") },
            { k: "rescheduling", label: t("cancel_reason_resched") },
            { k: "other", label: t("cancel_reason_other") },
          ].map(({ k, label }) => (
            <label key={k} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${reason === k ? "border-brand-green bg-emerald-50" : "border-brand-navy/15"}`}>
              <input type="radio" name="reason" value={k} checked={reason === k} onChange={() => setReason(k)} data-testid={`cancel-reason-${k}`} />
              <span className="text-sm text-brand-deep">{label}</span>
            </label>
          ))}
        </div>

        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} placeholder={t("cancel_detail_ph")} data-testid="cancel-detail" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15 text-sm resize-none mb-4" />

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-deep text-sm font-medium hover:border-brand-navy">{t("cancel_keep")}</button>
          <button onClick={confirm} disabled={busy || !reason} data-testid="cancel-confirm" className="flex-1 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : t("cancel_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== Family editor ==================== */

function FamilyEditor({ member, onCancel, onSaved }) {
  const { t } = useLang();
  const [name, setName] = useState(member?.name || "");
  const [relationship, setRelationship] = useState(member?.relationship || "");
  const [dob, setDob] = useState(member?.dob || "");
  const [phone, setPhone] = useState(member?.phone || "");
  const [medicaidId, setMedicaidId] = useState(member?.medicaid_id || "");
  const [insurancePlan, setInsurancePlan] = useState(member?.insurance_plan || "");
  const [doctorName, setDoctorName] = useState(member?.doctor_name || "");
  const [doctorPhone, setDoctorPhone] = useState(member?.doctor_phone || "");
  const [homeAddress, setHomeAddress] = useState(member?.home_address || "");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        relationship: relationship || null,
        dob: dob || null,
        phone: phone || null,
        medicaid_id: medicaidId || null,
        insurance_plan: insurancePlan || null,
        doctor_name: doctorName || null,
        doctor_phone: doctorPhone || null,
        home_address: homeAddress || null,
      };
      const res = member?.id
        ? await patientApi.editFamily(member.id, body)
        : await patientApi.addFamily(body);
      toast.success(t("fam_saved"));
      onSaved(res.member);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-brand-navy/15 rounded-xl p-4 space-y-2 bg-brand-surfaceAlt/50" data-testid="family-editor">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("profile_name")} required data-testid="fam-name" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <input value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder={t("fam_relationship_ph")} data-testid="fam-relationship" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} placeholder={t("profile_dob")} data-testid="fam-dob" className="px-3 py-2.5 rounded-lg border border-brand-navy/15" />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("profile_phone")} data-testid="fam-phone" className="px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      </div>
      <input value={medicaidId} onChange={(e) => setMedicaidId(e.target.value)} placeholder={t("profile_medicaid")} data-testid="fam-medicaid" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <input value={insurancePlan} onChange={(e) => setInsurancePlan(e.target.value)} placeholder={t("profile_plan")} data-testid="fam-plan" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <input value={doctorName} onChange={(e) => setDoctorName(e.target.value)} placeholder={t("profile_doctor")} data-testid="fam-doctor" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <input value={doctorPhone} onChange={(e) => setDoctorPhone(e.target.value)} placeholder={t("profile_doctor_phone")} data-testid="fam-doctor-phone" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
      <AddressAutocomplete value={homeAddress} onChange={setHomeAddress} placeholder={t("profile_home_ph")} testid="fam-address" />

      <div className="flex gap-2 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 px-3 py-2 rounded-full border border-brand-navy/15 text-sm text-brand-deep">{t("cancel_keep").split(" ")[0]}</button>
        <button type="submit" disabled={busy} data-testid="fam-save" className="flex-1 px-3 py-2 rounded-full bg-brand-navy text-white text-sm font-medium disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : t("profile_save")}
        </button>
      </div>
    </form>
  );
}

/* ==================== Profile drawer ==================== */

function ProfileDrawer({ user, onClose, onUpdated }) {
  const { t } = useLang();
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [dob, setDob] = useState(user.dob || "");
  const [medicaidId, setMedicaidId] = useState(user.medicaid_id || "");
  const [insurancePlan, setInsurancePlan] = useState(user.insurance_plan || "");
  const [doctorName, setDoctorName] = useState(user.doctor_name || "");
  const [doctorPhone, setDoctorPhone] = useState(user.doctor_phone || "");
  const [homeAddress, setHomeAddress] = useState(user.home_address || "");
  const [family, setFamily] = useState(user.family_members || []);
  const [editingFam, setEditingFam] = useState(null); // {id?} or "new"
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await patientApi.updateProfile({
        name, phone, dob: dob || null,
        medicaid_id: medicaidId || null,
        insurance_plan: insurancePlan || null,
        doctor_name: doctorName || null,
        doctor_phone: doctorPhone || null,
        home_address: homeAddress || null,
      });
      toast.success(t("profile_updated"));
      onUpdated({ ...res.user, family_members: family });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onFamSaved = (m) => {
    setFamily((prev) => {
      const i = prev.findIndex((x) => x.id === m.id);
      if (i === -1) return [...prev, m];
      const clone = [...prev];
      clone[i] = m;
      return clone;
    });
    setEditingFam(null);
    onUpdated({ ...user, name, phone, dob, medicaid_id: medicaidId, insurance_plan: insurancePlan,
      doctor_name: doctorName, doctor_phone: doctorPhone, home_address: homeAddress,
      family_members: (() => {
        const prev = family;
        const i = prev.findIndex((x) => x.id === m.id);
        if (i === -1) return [...prev, m];
        const c = [...prev]; c[i] = m; return c;
      })(),
    });
  };

  const onFamDelete = async (id) => {
    if (!window.confirm(t("fam_confirm_remove"))) return;
    try {
      await patientApi.deleteFamily(id);
      const next = family.filter((f) => f.id !== id);
      setFamily(next);
      toast.success(t("fam_removed"));
      onUpdated({ ...user, family_members: next });
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-brand-deep/50 backdrop-blur-sm px-4 overflow-auto py-10" onClick={onClose}>
      <div className="w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
        {/* Profile form */}
        <form onSubmit={save} className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl p-6 space-y-3" data-testid="profile-drawer">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-serif text-2xl text-brand-deep">{t("profile_title")}</h2>
            <button type="button" onClick={onClose} className="text-brand-muted hover:text-brand-deep"><XCircle className="w-5 h-5" /></button>
          </div>

          <label className="text-xs font-medium text-brand-deep">{t("profile_name")}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_phone")}</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="profile-phone" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_dob")}</label>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="profile-dob" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_home")}</label>
          <AddressAutocomplete value={homeAddress} onChange={setHomeAddress} placeholder={t("profile_home_ph")} testid="profile-address" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_medicaid")}</label>
          <input value={medicaidId} onChange={(e) => setMedicaidId(e.target.value)} data-testid="profile-medicaid" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_plan")}</label>
          <input value={insurancePlan} onChange={(e) => setInsurancePlan(e.target.value)} data-testid="profile-plan" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_doctor")}</label>
          <input value={doctorName} onChange={(e) => setDoctorName(e.target.value)} placeholder={t("profile_doctor_ph")} data-testid="profile-doctor" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="text-xs font-medium text-brand-deep">{t("profile_doctor_phone")}</label>
          <input value={doctorPhone} onChange={(e) => setDoctorPhone(e.target.value)} data-testid="profile-doctor-phone" className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15" />

          <button type="submit" disabled={busy} data-testid="profile-save" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full transition disabled:opacity-60 mt-4">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("profile_save")}
          </button>
        </form>

        {/* Family members */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl p-6 space-y-3" data-testid="family-section">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-brand-green" />
            <h3 className="font-serif text-xl text-brand-deep">{t("fam_section")}</h3>
          </div>
          <p className="text-xs text-brand-muted -mt-2 mb-2">{t("fam_subtitle")}</p>

          <div className="space-y-2">
            {family.length === 0 && !editingFam && (
              <div className="text-sm text-brand-muted text-center py-4">{t("fam_none")}</div>
            )}
            {family.map((f) => (
              editingFam === f.id ? (
                <FamilyEditor key={f.id} member={f} onCancel={() => setEditingFam(null)} onSaved={onFamSaved} />
              ) : (
                <div key={f.id} className="flex items-center justify-between gap-2 border border-brand-navy/10 rounded-xl p-3" data-testid={`fam-row-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-deep truncate">{f.name}</div>
                    <div className="text-xs text-brand-muted truncate">
                      {f.relationship || "—"}{f.medicaid_id ? ` · Medicaid: ${f.medicaid_id}` : ""}
                    </div>
                  </div>
                  <button type="button" onClick={() => setEditingFam(f.id)} data-testid={`fam-edit-${f.id}`} className="text-xs text-brand-navy hover:text-brand-green">{t("fam_edit")}</button>
                  <button type="button" onClick={() => onFamDelete(f.id)} data-testid={`fam-remove-${f.id}`} className="text-xs text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )
            ))}
            {editingFam === "new" && (
              <FamilyEditor onCancel={() => setEditingFam(null)} onSaved={onFamSaved} />
            )}
          </div>

          {editingFam !== "new" && (
            <button type="button" onClick={() => setEditingFam("new")} data-testid="fam-add" className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-brand-navy/30 text-brand-navy hover:bg-brand-surfaceAlt py-2.5 rounded-xl text-sm font-medium transition">
              <Plus className="w-4 h-4" /> {t("fam_add")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== Wallet drawer (saved cards) ==================== */

function WalletDrawer({ user, onClose, onChanged }) {
  const { t } = useLang();
  const [cards, setCards] = useState(user.saved_cards || []);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // pmId currently mutating

  // Keep cards in sync with the auth user object (updated after refresh)
  useEffect(() => { setCards(user.saved_cards || []); }, [user]);

  const addCard = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await patientApi.addCardCheckout(window.location.origin);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.message || "Could not start card setup");
      setBusy(false);
    }
  };

  const removeCard = async (pm) => {
    if (!window.confirm(`Remove ${(pm.brand || "card").toUpperCase()} ····${pm.last4}?`)) return;
    setPending(pm.stripe_payment_method_id);
    try {
      await patientApi.deleteCard(pm.stripe_payment_method_id);
      toast.success("Card removed");
      const u = await onChanged();
      setCards(u?.saved_cards || []);
    } catch (err) {
      toast.error(err.message || "Could not remove card");
    } finally {
      setPending(null);
    }
  };

  const makeDefault = async (pm) => {
    setPending(pm.stripe_payment_method_id);
    try {
      await patientApi.setDefaultCard(pm.stripe_payment_method_id);
      toast.success("Default card updated");
      const u = await onChanged();
      setCards(u?.saved_cards || []);
    } catch (err) {
      toast.error(err.message || "Could not update default");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="wallet-drawer"
      >
        <div className="px-5 py-4 border-b border-brand-navy/10 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-brand-green" />
            <h2 className="font-serif text-xl text-brand-deep">{t("wallet_title") || "Payment methods"}</h2>
          </div>
          <button onClick={onClose} data-testid="close-wallet" className="text-brand-muted hover:text-brand-deep">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-brand-muted">
            {t("wallet_subtitle") || "Manage the cards saved to your profile. Your default card is used for holds on new bookings."}
          </p>

          {cards.length === 0 ? (
            <div className="bg-brand-bg border border-brand-navy/10 rounded-2xl p-6 text-center" data-testid="wallet-empty">
              <CreditCard className="w-8 h-8 text-brand-muted mx-auto mb-2" />
              <p className="text-sm text-brand-muted">
                {t("wallet_empty") || "No cards on file yet. Add one now to unlock 1-tap booking."}
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="wallet-cards">
              {cards.map((pm) => {
                const pmId = pm.stripe_payment_method_id;
                const brand = (pm.brand || "card").toUpperCase();
                return (
                  <li
                    key={pmId}
                    className={`border rounded-2xl p-4 flex items-center gap-3 flex-wrap ${
                      pm.is_default ? "border-brand-green bg-emerald-50/40" : "border-brand-navy/10 bg-white"
                    }`}
                    data-testid={`wallet-card-${pmId}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-brand-deep text-base">{brand}</span>
                        <span className="text-brand-muted">····{pm.last4}</span>
                        {pm.is_default && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-full px-2 py-0.5" data-testid={`wallet-default-badge-${pmId}`}>
                            <CheckCircle2 className="w-3 h-3" /> Default
                          </span>
                        )}
                      </div>
                      {pm.added_at && (
                        <div className="text-[11px] text-brand-muted mt-0.5">
                          Added {new Date(pm.added_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!pm.is_default && (
                        <button
                          onClick={() => makeDefault(pm)}
                          disabled={pending === pmId}
                          data-testid={`wallet-set-default-${pmId}`}
                          className="text-xs font-semibold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 px-3 py-1.5 rounded-full disabled:opacity-50"
                        >
                          {pending === pmId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Set default"}
                        </button>
                      )}
                      <button
                        onClick={() => removeCard(pm)}
                        disabled={pending === pmId}
                        data-testid={`wallet-delete-${pmId}`}
                        className="text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        {pending === pmId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5" /> Remove</>}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            onClick={addCard}
            disabled={busy}
            data-testid="wallet-add-card"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white font-semibold py-3 rounded-full disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("wallet_add_cta") || "Add a new card"}
          </button>

          <div className="text-[11px] text-brand-muted text-center">
            Cards are stored securely with Stripe — 1st Touch never sees your full number.
          </div>
        </div>
      </div>
    </div>
  );
}


/* ==================== Dashboard ==================== */

function Dashboard() {
  const { t } = useLang();
  const { state, logout, setUser, refresh } = usePatientAuth();
  const [rides, setRides] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard");
  const [openRideId, setOpenRideId] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [pushStatus, setPushStatus] = useState("default"); // default | granted | denied | unsupported
  const [bookPrefill, setBookPrefill] = useState(null);

  const user = state.user;

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        patientApi.listRides(),
        patientApi.listEstimates().catch(() => ({ estimates: [] })),
      ]);
      setRides(r.rides);
      setEstimates(e.estimates || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const deleteEstimate = async (id) => {
    try {
      await patientApi.deleteEstimate(id);
      setEstimates((prev) => prev.filter((x) => x.id !== id));
      toast.success("Estimate removed");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const bookFromEstimate = (est) => {
    setBookPrefill({
      trip_class: est.trip_class,
      mobility: est.mobility,
      pickup: est.pickup,
      dropoff: est.dropoff,
    });
    setView("book");
  };

  // Register SW + check push status
  useEffect(() => {
    if (!pushSupported()) { setPushStatus("unsupported"); return; }
    registerPatientServiceWorker();
    setPushStatus(Notification.permission);
    // Auto-subscribe if already granted
    if (Notification.permission === "granted") {
      subscribePatient().catch(() => {});
    }
  }, []);

  // Handle ?pay=<ride_id> — auto-launch checkout for series payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payRideId = params.get("pay");
    if (payRideId) {
      // Strip the query param
      window.history.replaceState({}, "", "/patient");
      setOpenRideId(payRideId);
      setView("ride");
    }
  }, []);

  // Handle ?paid=<ride_id>&session_id=<cs_...> — return from series-payment Checkout.
  // Poll the reconciler until the ride flips to `held` (or bail with a friendly toast).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidRideId = params.get("paid");
    const paidSid = params.get("session_id");
    const cancelledId = params.get("paid_cancelled");
    if (cancelledId) {
      window.history.replaceState({}, "", "/patient");
      toast.info("Payment cancelled — this ride hasn't been authorized yet.");
      return;
    }
    if (!paidRideId || !paidSid) return;
    window.history.replaceState({}, "", "/patient");
    let cancelled = false;
    const DELAYS = [1000, 1500, 2000, 3000, 4000, 6000, 9000, 13000];
    let attempt = 0;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const t = toast.loading("Confirming your authorization…");
    (async () => {
      while (!cancelled && attempt < DELAYS.length) {
        attempt += 1;
        try {
          const r = await fetch(`${backend}/api/nemt/rides/status/${encodeURIComponent(paidSid)}`, { cache: "no-store" });
          if (r.ok) {
            const d = await r.json();
            if (d.status === "held" || d.status === "completed") {
              toast.success("Your card is authorized — the ride is confirmed.", { id: t });
              return;
            }
          }
        } catch { /* keep trying */ }
        await new Promise((res) => setTimeout(res, DELAYS[attempt - 1]));
      }
      if (!cancelled) {
        toast.warning(
          "Your bank hasn't confirmed the hold yet. If your card has been debited but the ride still says pending, call dispatch at (404) 590-4621.",
          { id: t, duration: 12000 },
        );
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle ?wallet=added|cancelled — return from Stripe Setup Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const walletState = params.get("wallet");
    const sessionId = params.get("session_id");
    if (!walletState) return;
    window.history.replaceState({}, "", "/patient");
    if (walletState === "cancelled") {
      toast.info("Card setup cancelled — no card was saved.");
      setShowWallet(true);
      return;
    }
    if (walletState === "added" && sessionId) {
      (async () => {
        try {
          const res = await patientApi.confirmAddedCard(sessionId);
          toast.success(`Card added: ${res.brand?.toUpperCase() || "Card"} ····${res.last4 || ""}`);
          await refresh();
        } catch (err) {
          toast.error(err.message || "Could not save card");
        } finally {
          setShowWallet(true);
        }
      })();
    }
  }, [refresh]);

  const enablePush = async () => {
    const res = await subscribePatient();
    setPushStatus(Notification.permission);
    if (res.ok) {
      toast.success(t("push_success"));
    } else if (res.reason === "denied") {
      toast.error(t("push_denied"));
    }
  };

  if (view === "book") return <BookRide user={user} prefill={bookPrefill} onBack={() => { setView("dashboard"); setBookPrefill(null); }} onBooked={() => { setView("dashboard"); setBookPrefill(null); load(); }} />;
  if (view === "ride" && openRideId) return <RideDetail rideId={openRideId} onBack={() => { setView("dashboard"); setOpenRideId(null); }} onRefresh={load} />;

  const active = rides.filter((r) => ["held", "insurance_pending", "pending_payment", "series_pending_payment"].includes(r.status));
  const past = rides.filter((r) => ["completed", "cancelled"].includes(r.status));

  return (
    <div className="min-h-screen bg-brand-bg pb-16">
      <div className="bg-brand-deep text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">{t("auth_portal")}</div>
            <h1 className="font-serif text-3xl mt-1">{t("dash_hi")} {(user.name || "").split(" ")[0] || "there"}</h1>
            <p className="text-sm text-white/85 mt-1">{t("dash_welcome_subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <LangSwitch compact />
            <button onClick={() => setShowWallet(true)} data-testid="open-wallet" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <Wallet className="w-4 h-4" /> {t("dash_wallet") || "Wallet"}
            </button>
            <button onClick={() => setShowProfile(true)} data-testid="open-profile" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <Edit3 className="w-4 h-4" /> {t("dash_profile")}
            </button>
            <button onClick={logout} data-testid="patient-signout" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <LogOut className="w-4 h-4" /> {t("dash_signout")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8 mt-8 space-y-6">
        <button onClick={() => setView("book")} data-testid="open-book"
          className="w-full flex items-center justify-between gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-5 px-6 rounded-2xl transition shadow-lg">
          <div className="text-left">
            <div className="text-lg leading-tight">{t("dash_book_title")}</div>
            <div className="text-xs text-white/80 font-normal mt-0.5">{t("dash_book_subtitle")}</div>
          </div>
          <Plus className="w-6 h-6" />
        </button>

        {pushStatus !== "granted" && pushStatus !== "unsupported" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3" data-testid="push-banner">
            <BellOff className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900">{t("push_enable_title")}</div>
              <div className="text-xs text-brand-muted mt-0.5">{t("push_enable_body")}</div>
            </div>
            <button onClick={enablePush} data-testid="patient-enable-push" className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded-full">
              <Bell className="w-3.5 h-3.5 inline mr-1" /> {t("push_enable_cta")}
            </button>
          </div>
        )}
        {pushStatus === "granted" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex items-center gap-2 text-xs text-emerald-800" data-testid="push-status-on">
            <Bell className="w-4 h-4" /> {t("push_enabled")}
          </div>
        )}

        {(!user.medicaid_id || !user.doctor_name || !user.home_address) && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900">{t("dash_finish_profile_title")}</div>
              <div className="text-xs text-brand-muted mt-0.5">{t("dash_finish_profile_body")}</div>
            </div>
            <button onClick={() => setShowProfile(true)} className="text-xs font-semibold text-amber-800 hover:text-amber-900">{t("dash_finish_profile_cta")}</button>
          </div>
        )}

        {estimates.length > 0 && (
          <section data-testid="saved-estimates-section">
            <h2 className="font-serif text-xl text-brand-deep mb-3 flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-brand-green" /> Saved estimates
            </h2>
            <div className="space-y-2">
              {estimates.map((est) => (
                <div
                  key={est.id}
                  className="bg-white border border-brand-navy/10 rounded-2xl p-4 flex items-center gap-3 flex-wrap"
                  data-testid={`saved-estimate-${est.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-brand-deep">
                      ${(est.low_cents / 100).toFixed(2)} – ${(est.high_cents / 100).toFixed(2)}
                    </div>
                    <div className="text-xs text-brand-muted truncate">
                      {titleCase(est.trip_class.replace(/_/g, " "))} · {est.mobility}
                      {est.miles ? ` · ${est.miles} mi` : ""}
                      {est.round_trip ? " · round trip" : ""}
                    </div>
                    {(est.pickup || est.dropoff) && (
                      <div className="text-[11px] text-brand-muted truncate mt-0.5">
                        {est.pickup || "?"} → {est.dropoff || "?"}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => bookFromEstimate(est)}
                      data-testid={`book-estimate-${est.id}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-2 rounded-full"
                    >
                      <Plus className="w-3.5 h-3.5" /> Book this
                    </button>
                    <button
                      onClick={() => deleteEstimate(est.id)}
                      data-testid={`delete-estimate-${est.id}`}
                      className="text-brand-muted hover:text-red-600 p-2"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-serif text-2xl text-brand-deep mb-3">{t("dash_upcoming")}</h2>
          {loading ? (<div className="text-center py-8 text-brand-muted text-sm">{t("dash_loading")}</div>)
            : active.length === 0 ? (
              <div className="bg-white border border-brand-navy/10 rounded-2xl p-8 text-center">
                <Clock className="w-8 h-8 text-brand-green mx-auto" />
                <p className="text-sm text-brand-muted mt-3">{t("dash_no_rides")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {active.map((r) => <RideRow key={r.id} ride={r} onOpen={() => { setOpenRideId(r.id); setView("ride"); }} />)}
              </div>
            )}
        </section>

        {past.length > 0 && (
          <section>
            <h2 className="font-serif text-xl text-brand-deep mb-3">{t("dash_past")}</h2>
            <div className="space-y-3">
              {past.map((r) => <RideRow key={r.id} ride={r} onOpen={() => { setOpenRideId(r.id); setView("ride"); }} />)}
            </div>
          </section>
        )}
      </div>

      {showProfile && <ProfileDrawer user={user} onClose={() => setShowProfile(false)} onUpdated={(u) => setUser(u)} />}
      {showWallet && <WalletDrawer user={user} onClose={() => setShowWallet(false)} onChanged={refresh} />}
    </div>
  );
}

function RideRow({ ride, onOpen }) {
  const tone = {
    held: "bg-amber-100 text-amber-800",
    pending_payment: "bg-blue-100 text-blue-800",
    series_pending_payment: "bg-amber-100 text-amber-800",
    insurance_pending: "bg-purple-100 text-purple-800",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-slate-100 text-slate-700",
  }[ride.status] || "bg-slate-100 text-slate-700";

  return (
    <button onClick={onOpen} data-testid={`ride-row-${ride.id}`}
      className="w-full bg-white border border-brand-navy/10 rounded-2xl p-5 text-left hover:border-brand-green transition flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-serif text-lg text-brand-deep">{ride.trip_date}</span>
          <span className="text-brand-muted text-sm">{ride.trip_time}</span>
          <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${tone}`}>{ride.status?.replace(/_/g, " ")}</span>
          {ride.for_kind === "family" && ride.for_name && (
            <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-brand-navy/10 text-brand-navy"><Users className="w-3 h-3 inline mr-0.5" /> {ride.for_name}</span>
          )}
        </div>
        <div className="text-sm text-brand-deep mt-1 truncate"><MapPin className="w-3.5 h-3.5 inline text-emerald-600 mr-1" />{ride.pickup}</div>
        <div className="text-xs text-brand-muted mt-0.5 truncate">→ {ride.dropoff}</div>
        {ride.driver_name && ride.driver_ride_status !== "completed" && (
          <div className="text-xs text-brand-green mt-1"><Car className="w-3.5 h-3.5 inline mr-1" /> {ride.driver_name} · {ride.driver_ride_status === "en_route" ? "On the way" : titleCase(ride.driver_ride_status || "assigned")}</div>
        )}
      </div>
      <ChevronRight className="w-5 h-5 text-brand-muted" />
    </button>
  );
}

/* ==================== Root ==================== */

function PatientRoot() {
  const { state } = usePatientAuth();
  const [authMode, setAuthMode] = useState("signin");

  if (state === null) return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  if (state === false) return <AuthCard mode={authMode} onSwitch={setAuthMode} />;
  return <Dashboard />;
}

export default function Patient() {
  return (
    <LangProvider>
      <PatientAuthProvider>
        <PatientRoot />
      </PatientAuthProvider>
    </LangProvider>
  );
}
